import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BUSINESS, SERVICES, LOGO_FULL, LOGO_MARK, BRAND_KIT } from "../data/content";
import { Download, Phone, Mail, MapPin, FileText, Printer, Palette, Type } from "lucide-react";

const COLOURS = [
  { name: "Signal Red", hex: "#E31E24", swatch: "#E31E24" },
  { name: "Jet Black", hex: "#070708", swatch: "#070708" },
  { name: "White", hex: "#FFFFFF", swatch: "#FFFFFF" },
];

const TYPEFACES = [
  { name: "Outfit", use: "Headings", cls: "font-display font-bold" },
  { name: "IBM Plex Sans", use: "Body text", cls: "font-body" },
  { name: "JetBrains Mono", use: "Labels & tech accents", cls: "font-mono" },
];

export default function BrandPage() {
  return (
    <div className="min-h-screen bg-navy text-white font-body">
      <Navbar />
      <main className="pt-24 pb-24">
        <section className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">// Hylyte Digital</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-bold">Brand kit &amp; stationery</h1>
          <p className="mt-4 text-muted max-w-2xl leading-relaxed">
            Our logo, brand colours, typefaces and print-ready stationery — all in one place.
            Download the full brand kit (letterhead, business cards and brand sheet) as a print-ready PDF.
          </p>

          {/* Logo downloads */}
          <div className="mt-14">
            <div className="flex items-center gap-2.5 mb-5">
              <Palette size={16} className="text-signal" />
              <h2 className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Logos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="glass rounded-2xl p-6 flex flex-col gap-4">
                <div className="bg-navy-lighter rounded-xl p-6 flex items-center justify-center min-h-[120px]">
                  <img src={LOGO_FULL} alt="Hylyte Digital full logo" className="h-12 w-auto" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted">Full lockup (PNG)</p>
                  <a href={LOGO_FULL} download data-testid="download-logo-full"
                    className="inline-flex items-center gap-1.5 text-sm text-signal hover:text-signal-hover">
                    <Download size={14} /> Download
                  </a>
                </div>
              </div>
              <div className="glass rounded-2xl p-6 flex flex-col gap-4">
                <div className="bg-navy-lighter rounded-xl p-6 flex items-center justify-center min-h-[120px]">
                  <img src={LOGO_MARK} alt="Hylyte Digital HD monogram" className="h-16 w-16" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted">HD monogram (PNG)</p>
                  <a href={LOGO_MARK} download data-testid="download-logo-mark"
                    className="inline-flex items-center gap-1.5 text-sm text-signal hover:text-signal-hover">
                    <Download size={14} /> Download
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Colours + Type */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <Palette size={16} className="text-signal" />
                <h2 className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Brand colours</h2>
              </div>
              <div className="flex flex-col gap-3">
                {COLOURS.map((c) => (
                  <div key={c.hex} className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-lg border border-white/10 shrink-0" style={{ background: c.swatch }} />
                    <div>
                      <p className="text-sm font-semibold text-white">{c.name}</p>
                      <p className="font-mono text-xs text-muted">{c.hex}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <Type size={16} className="text-signal" />
                <h2 className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Typography</h2>
              </div>
              <div className="flex flex-col gap-4">
                {TYPEFACES.map((t) => (
                  <div key={t.name}>
                    <p className={`text-lg text-white ${t.cls}`}>{t.name}</p>
                    <p className="text-xs text-muted">{t.use}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Business card preview */}
          <div className="mt-14">
            <div className="flex items-center gap-2.5 mb-5">
              <Printer size={16} className="text-signal" />
              <h2 className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted">Business card</h2>
            </div>
            <div className="glass rounded-2xl p-6 flex flex-col sm:flex-row gap-6 items-center">
              <div className="bg-navy-lighter rounded-xl p-6 w-full sm:w-72 flex flex-col gap-3 border border-white/10">
                <img src={LOGO_MARK} alt="" className="h-10 w-10" />
                <div>
                  <p className="font-display font-bold text-white text-lg leading-tight">HYLYTE</p>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-muted">DIGITAL</p>
                </div>
                <p className="text-xs text-muted">We design &amp; develop customised web applications</p>
              </div>
              <div className="bg-navy-lighter rounded-xl p-6 w-full sm:w-72 flex flex-col gap-2 border border-white/10">
                <p className="font-semibold text-white">Your Name</p>
                <p className="text-xs text-muted">Role / Title</p>
                <p className="text-xs text-muted mt-2">{BUSINESS.phone}</p>
                <p className="text-xs text-muted">{BUSINESS.email}</p>
                <p className="text-xs text-muted">www.hylytedigital.com.au</p>
              </div>
            </div>
          </div>

          {/* Brand kit download */}
          <div className="mt-14 glass rounded-3xl p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 neon-glow">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-signal/10 border border-signal/30 text-signal shrink-0">
                <FileText size={20} />
              </span>
              <div>
                <h2 className="font-display text-xl font-semibold text-white">Print-ready brand kit (PDF)</h2>
                <p className="text-sm text-muted mt-1">Letterhead, business cards and brand sheet — one A4 PDF, ready to send to the printer.</p>
              </div>
            </div>
            <a href={BRAND_KIT} download data-testid="download-brand-kit"
              className="inline-flex items-center gap-2 rounded-full bg-signal px-6 py-3 font-semibold text-white hover:bg-signal-hover transition-colors shrink-0">
              <Download size={16} /> Download PDF
            </a>
          </div>

          {/* Contact strip */}
          <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-muted">
            <a href={BUSINESS.phoneHref} className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={14} className="text-signal" /> {BUSINESS.phone}
            </a>
            <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
              <Mail size={14} className="text-signal" /> {BUSINESS.email}
            </a>
            <p className="flex items-center gap-2">
              <MapPin size={14} className="text-signal" /> {BUSINESS.address}
            </p>
          </div>

          {/* Services recap */}
          <div className="mt-10">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted mb-3">What we do</p>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <span key={s.id} className="glass rounded-full px-4 py-1.5 text-sm text-white">{s.title}</span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
